import React from 'react';
import { Helmet } from 'react-helmet-async';
import { LOGO_ABSOLUTE_URL } from '../../lib/branding';

export const SEOHead = ({
  title,
  description,
  canonical,
  ogImage,
  ogType = 'website',
  schemaData,
}) => {
  const siteName = 'Passion of Aroma';
  const defaultDescription = 'Premium fragrance wholesaler & distributor in Ahmedabad. Authorized dealer of luxury perfumes, deodorants, cosmetics from top brands.';
  const defaultImage = LOGO_ABSOLUTE_URL;

  const fullTitle = title ? `${title} | ${siteName}` : siteName;
  const metaDescription = description || defaultDescription;
  const metaImage = ogImage || defaultImage;

  return (
    <Helmet>
      {/* Basic Meta */}
      <title>{fullTitle}</title>
      <meta name="description" content={metaDescription} />
      {canonical && <link rel="canonical" href={canonical} />}

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:type" content={ogType} />
      <meta property="og:image" content={metaImage} />
      <meta property="og:site_name" content={siteName} />
      {canonical && <meta property="og:url" content={canonical} />}

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={metaImage} />

      {/* Schema.org JSON-LD */}
      {schemaData && (
        <script type="application/ld+json">
          {JSON.stringify(schemaData)}
        </script>
      )}
    </Helmet>
  );
};

// Organization Schema
export const getOrganizationSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Passion of Aroma',
  url: 'https://passionofaroma.com',
  logo: LOGO_ABSOLUTE_URL,
  foundingDate: '2013',
  description: 'Authorized wholesaler & distributor of premium fragrances since 2013. Ahmedabad\'s largest exporter of perfumes, deodorants, and cosmetics.',
  address: {
    '@type': 'PostalAddress',
    addressLocality: 'Ahmedabad',
    addressRegion: 'Gujarat',
    addressCountry: 'India',
  },
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: '+91-98765-43210',
    contactType: 'sales',
    availableLanguage: ['English', 'Hindi', 'Gujarati'],
  },
});

// LocalBusiness Schema
export const getLocalBusinessSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'LocalBusiness',
  name: 'Passion of Aroma',
  image: LOGO_ABSOLUTE_URL,
  '@id': 'https://passionofaroma.com',
  url: 'https://passionofaroma.com',
  telephone: '+91-98765-43210',
  address: {
    '@type': 'PostalAddress',
    addressLocality: 'Ahmedabad',
    addressRegion: 'Gujarat',
    postalCode: '380001',
    addressCountry: 'IN',
  },
  openingHoursSpecification: {
    '@type': 'OpeningHoursSpecification',
    dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
    opens: '10:00',
    closes: '19:00',
  },
  priceRange: '$$',
});

// Product Schema
export const getProductSchema = (product, category, brand) => ({
  '@context': 'https://schema.org',
  '@type': 'Product',
  name: product.name,
  description: product.description,
  image: product.images?.[0],
  brand: {
    '@type': 'Brand',
    name: brand?.name || 'Unknown',
  },
  category: category?.name || 'Fragrance',
  offers: {
    '@type': 'Offer',
    availability: product.availability === 'In Stock' 
      ? 'https://schema.org/InStock' 
      : 'https://schema.org/PreOrder',
    priceCurrency: 'INR',
    seller: {
      '@type': 'Organization',
      name: 'Passion of Aroma',
    },
  },
});

// FAQ Schema
export const getFAQSchema = (faqs) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer,
    },
  })),
});

// BreadcrumbList Schema
export const getBreadcrumbSchema = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: item.url,
  })),
});

export default SEOHead;
